import { ButtonSecondary, Container, Text, type OptionProps } from '@amelio/ui-library';
import { useAtomValue } from 'jotai';

import { CollapsibleSectionHeading } from '../CollapsibleSectionHeading';
import { NonCollapsingContent } from '../NonCollapsingContent';

import type { PaginatedResponse } from '@/types/api';
import type { ReviewTemplate } from '@/types/review/ReviewTemplate';
import type {
    FetchNextPageOptions,
    InfiniteData,
    InfiniteQueryObserverResult,
} from '@tanstack/react-query';

import { userInstanceAtom } from '@/atoms/userDetails';
import { NewTemplateCard } from '@/components/TemplateCard/NewTemplateCard';
import { TemplateCard } from '@/components/TemplateCard/TemplateCard';
import { TemplateCardSkeleton } from '@/components/TemplateCard/TemplateCardSkeleton';
import { useAmelioTranslations } from '@/hooks';
import { useReplaceDynamicTags } from '@/hooks/useReplaceDynamicTags';
import { getLocalizedText } from '@/utils/localization';

type TemplateSectionProps = {
    heading: string;
    isCreatingTemplate: boolean;
    companyTemplates: ReviewTemplate[];
    isLoadingTemplates: boolean;
    hasMoreTemplates: boolean;
    isFetchingNextPage: boolean;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
    templateCardActions?: (reviewTemplate: ReviewTemplate) => OptionProps[];
    onImportTemplate: () => void;
    onStartBuilding: () => void;
    onTemplateCardClick?: (templateId: string) => void;
    loadMoreTemplates: (
        options?: FetchNextPageOptions | undefined,
    ) => Promise<
        InfiniteQueryObserverResult<InfiniteData<PaginatedResponse<ReviewTemplate>, unknown>, Error>
    >;
};

export const TemplateSection = ({
    heading,
    isCreatingTemplate,
    companyTemplates,
    isLoadingTemplates,
    hasMoreTemplates,
    isFetchingNextPage,
    isCollapsed = false,
    onToggleCollapse,
    templateCardActions,
    onImportTemplate,
    onStartBuilding,
    onTemplateCardClick,
    loadMoreTemplates,
}: TemplateSectionProps) => {
    const { t } = useAmelioTranslations();
    const replaceDynamicTags = useReplaceDynamicTags();
    const loggedUser = useAtomValue(userInstanceAtom);

    const getTemplateCardActions = (reviewTemplate: ReviewTemplate) =>
        templateCardActions ? templateCardActions(reviewTemplate) : ([] as OptionProps[]);

    return (
        <>
            <CollapsibleSectionHeading
                heading={heading}
                isCollapsed={isCollapsed}
                onToggleCollapse={onToggleCollapse}
            >
                {!loggedUser?.isAmelioAdmin && (
                    <ButtonSecondary
                        size={'md'}
                        className={'mt-4 border border-gray-200 hover:bg-gray-100 sm:mt-0'}
                        onClick={(event) => {
                            event.stopPropagation();
                            onImportTemplate();
                        }}
                    >
                        <Text
                            weight={'medium'}
                            size={'small'}
                        >
                            {t('templateManagement.template.importTemplateText')}
                        </Text>
                    </ButtonSecondary>
                )}
            </CollapsibleSectionHeading>

            {!isCollapsed && (
                <NonCollapsingContent>
                    <div
                        className={
                            'grid grid-cols-1 gap-6 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3'
                        }
                    >
                        <NewTemplateCard
                            title={t('evaluation360SelectTemplate.startFromScratch.title')}
                            description={t('evaluation360SelectTemplate.startBuilding')}
                            iconName={'essential-add'}
                            onButtonClick={onStartBuilding}
                            isCreatingTemplate={isCreatingTemplate}
                        />

                        {isLoadingTemplates && !isFetchingNextPage ? (
                            <>
                                {Array.from({ length: 5 }).map((_, index) => (
                                    <TemplateCardSkeleton key={index} />
                                ))}
                            </>
                        ) : (
                            <>
                                {companyTemplates?.map((template) => (
                                    <TemplateCard
                                        key={template.id}
                                        description={template.description || ''}
                                        iconName={'electronics-monitor'}
                                        title={replaceDynamicTags({
                                            text: getLocalizedText(template.title),
                                        })}
                                        questionCount={template.questions?.length ?? 0}
                                        actions={getTemplateCardActions(template)}
                                        status={template.status}
                                        onCardClick={
                                            onTemplateCardClick
                                                ? () => onTemplateCardClick(template.id)
                                                : undefined
                                        }
                                    />
                                ))}

                                {isFetchingNextPage && (
                                    <>
                                        {Array.from({ length: 6 }).map((_, index) => (
                                            <TemplateCardSkeleton key={index} />
                                        ))}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </NonCollapsingContent>
            )}

            {!isCollapsed && hasMoreTemplates && (
                <Container className={'flex w-full justify-center'}>
                    <ButtonSecondary
                        onClick={(event) => {
                            event.stopPropagation();
                            loadMoreTemplates();
                        }}
                        className={'mt-8 items-center border border-gray-200'}
                    >
                        {t('common.viewMore')}
                    </ButtonSecondary>
                </Container>
            )}
        </>
    );
};
